import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import axios from "../../api/axios";
import { getAttributeValue, getFileLinkIfExist } from "../../utils/utils";
import APRComment from "./APRComment.js/AprComments";
import APRmemo from "./APRComment.js/APRmemo";
import { formatDimensionLabel, isBlankDimension } from "./AprViewToggle";

const PROJECT_PROGRAM = "g3wMUKEMmH3";
const PROJECT_TYPE_ATTRIBUTE = "wtf3BR2YO8w";
const PROJECT_TYPE_VALUE = "Programme";
const IMPLEMENTATION_STAGE = "rRWa5ghYO35";
const EXPENDITURE_ELEMENT = "jr8gk707kAw";
const IMPLEMENTATION_STATUS_ELEMENT = "f1T48vHfJc1";
// Common project/programme evidence picture data elements used in DDDP trackers
const PICTURE_DATA_ELEMENTS = ["MjPmcbueEdU", "xjRCTFFiMA3", "LAe1t59jYNT"];

function getStageEventValue(reports, trackedEntity, stageId, dataElementId) {
  const stageEvent = reports.find(
    (report) =>
      report.trackedEntity === trackedEntity
      && report.programStage === stageId
      && Array.isArray(report.dataValues)
      && report.dataValues.length > 0,
  );

  if (!stageEvent) {
    return "";
  }

  const match = stageEvent.dataValues.find(
    (dataValue) => dataValue.dataElement === dataElementId,
  );

  return match?.value ?? "";
}

function resolvePictureLink(reports, trackedEntity) {
  for (const dataElementUid of PICTURE_DATA_ELEMENTS) {
    const eventUid = getFileLinkIfExist(reports, dataElementUid, trackedEntity);
    if (eventUid) {
      return {
        href: `https://dddp.gov.gh/api/events/files?eventUid=${eventUid}&dataElementUid=${dataElementUid}`,
        dataElementUid,
      };
    }
  }
  return null;
}

const Table_8 = forwardRef(({ year, district, period, hideTableDis }, ref) => {
  const [tableData, setTableData] = useState([]);

  const selectedYear = useMemo(() => {
    const parsed = parseInt(year, 10);
    return Number.isFinite(parsed) ? parsed : new Date().getFullYear();
  }, [year]);

  useEffect(() => {
    getProjects();
  }, [selectedYear, district, period]);

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "Table_8",
      tableData,
    }),
  }));

  async function getProjects() {
    if (!district) {
      setTableData([]);
      return;
    }

    try {
      const startDate = `${selectedYear}-01-01`;
      const endDate = `${selectedYear}-12-31`;

      const [entitiesResp, eventsResp] = await Promise.all([
        axios.get(
          `/tracker/trackedEntities?orgUnit=${district}&program=${PROJECT_PROGRAM}&filter=${PROJECT_TYPE_ATTRIBUTE}:EQ:${PROJECT_TYPE_VALUE}&startDate=${startDate}&endDate=${endDate}&pageSize=5000`,
        ),
        axios.get(
          `/tracker/events?program=${PROJECT_PROGRAM}&orgUnit=${district}&startDate=${startDate}&endDate=${endDate}&pageSize=5000`,
        ),
      ]);

      const programmes = entitiesResp.data.instances || [];
      const reports = eventsResp.data.instances || [];

      if (programmes.length === 0) {
        setTableData([]);
        return;
      }

      const temps = programmes.map((programme, idx) => {
        const contractSum = getAttributeValue("Contract Sum", programme);
        const expenditureRaw = getStageEventValue(
          reports,
          programme.trackedEntity,
          IMPLEMENTATION_STAGE,
          EXPENDITURE_ELEMENT,
        );
        const implementationStatusRaw = getStageEventValue(
          reports,
          programme.trackedEntity,
          IMPLEMENTATION_STAGE,
          IMPLEMENTATION_STATUS_ELEMENT,
        );

        const expenditure = parseFloat(expenditureRaw) || 0;
        const contractSumValue = parseFloat(contractSum) || 0;
        const rawDimension = getAttributeValue("Development Dimension", programme);
        const picture = resolvePictureLink(reports, programme.trackedEntity);

        return {
          no: idx + 1,
          description: getAttributeValue("Description", programme),
          dimension: isBlankDimension(rawDimension) ? "" : formatDimensionLabel(rawDimension),
          contractSum: contractSum === "N/A" ? "" : contractSum,
          fundingSource: getAttributeValue("Primary Funding Source", programme),
          dateStarted: getAttributeValue("Start Date", programme),
          expectedCompletion: getAttributeValue("Expected Completion Date", programme),
          expenditure: expenditureRaw || 0,
          outstanding: contractSumValue - expenditure,
          implementationStatus: implementationStatusRaw || "",
          beneficiariesMale: getAttributeValue("Total Male Beneficiary", programme),
          beneficiariesFemale: getAttributeValue("Total Female Beneficiary", programme),
          remarks: getAttributeValue("Remarks", programme),
          pictureHref: picture?.href || "",
        };
      });

      setTableData(temps);
    } catch (err) {
      console.error("Error in getProjects:", err);
      setTableData([]);
    }
  }

  const tableStyle = {
    border: "1px solid #000",
    borderCollapse: "collapse",
    width: "100%",
    marginTop: "12px",
    minWidth: "1200px",
  };

  const thStyle = {
    border: "1px solid #000",
    fontWeight: "bold",
    backgroundColor: "#d4edda",
    textAlign: "center",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  };

  const tdStyle = {
    border: "1px solid #000",
    verticalAlign: "top",
    padding: "8px",
  };

  const clean = (value) => (value === "N/A" ? "" : value);

  return (
    <div className="col-12">
      <h3>Table 8 – Programmes (Non-Physical Projects) Register</h3>
      <div className="card">
        <div className="card-header"></div>
        <div className="card-body">
          <p style={{ textAlign: "justify", marginBottom: "1rem" }}>
            This register covers non-physical programmes implemented during the year. Scroll
            horizontally to review the full register as one coherent table. Where photographic
            evidence has been uploaded, use the link in the Pictures column to open the image.
          </p>
          <APRmemo
            year={year}
            districtId={district}
            tableCommentedId={`table8-${year}`}
            hideTableDis={hideTableDis}
          />

          <div className="table-responsive">
            <table className="table table-bordered" style={tableStyle}>
              <thead>
                <tr style={{ backgroundColor: "#d4edda", fontWeight: "bold" }}>
                  <th style={thStyle} rowSpan="2">#</th>
                  <th style={thStyle} rowSpan="2">Programme Description</th>
                  <th style={thStyle} rowSpan="2">Development Dimension</th>
                  <th style={thStyle} rowSpan="2">Amount Involved GH¢</th>
                  <th style={thStyle} rowSpan="2">Source of Funding</th>
                  <th style={thStyle} rowSpan="2">Date Started</th>
                  <th style={thStyle} rowSpan="2">Expected Date of Completion</th>
                  <th style={thStyle} colSpan="2">Balance</th>
                  <th style={thStyle} rowSpan="2">Implementation Status (%)</th>
                  <th style={thStyle} rowSpan="2">Pictures</th>
                  <th style={thStyle} colSpan="2">Total Beneficiaries</th>
                  <th style={thStyle} rowSpan="2">Remarks</th>
                </tr>
                <tr style={{ backgroundColor: "#d4edda", fontWeight: "bold" }}>
                  <th style={thStyle}>Expenditure to Date</th>
                  <th style={thStyle}>Outstanding Balance</th>
                  <th style={thStyle}>Male</th>
                  <th style={thStyle}>Female</th>
                </tr>
              </thead>
              <tbody>
                {tableData.length > 0 ? (
                  tableData.map((row, index) => (
                    <tr key={index}>
                      <td style={tdStyle}>{row.no}</td>
                      <td style={tdStyle}>{clean(row.description)}</td>
                      <td style={tdStyle}>{row.dimension}</td>
                      <td style={tdStyle}>{row.contractSum}</td>
                      <td style={tdStyle}>{clean(row.fundingSource)}</td>
                      <td style={tdStyle}>{clean(row.dateStarted)}</td>
                      <td style={tdStyle}>{clean(row.expectedCompletion)}</td>
                      <td style={tdStyle}>{row.expenditure}</td>
                      <td style={tdStyle}>{row.outstanding}</td>
                      <td style={tdStyle}>{row.implementationStatus}</td>
                      <td style={tdStyle}>
                        {row.pictureHref ? (
                          <a
                            href={row.pictureHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-2 text-primary fw-bold text-decoration-underline"
                            title="Click to view uploaded picture"
                          >
                            View Picture
                          </a>
                        ) : (
                          ""
                        )}
                      </td>
                      <td style={tdStyle}>{clean(row.beneficiariesMale)}</td>
                      <td style={tdStyle}>{clean(row.beneficiariesFemale)}</td>
                      <td style={tdStyle}>{clean(row.remarks)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="14" style={{ textAlign: "center" }}>
                      No data available for {selectedYear}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <APRComment
            data={tableData}
            year={year}
            districtId={district}
            tableCommentedId={`table8-${year}`}
            hideTableDis={hideTableDis}
          >
            {({ renderCommentInput, renderCommentList }) => (
              <>
                {renderCommentInput()}
                {renderCommentList()}
              </>
            )}
          </APRComment>
        </div>
      </div>
    </div>
  );
});

export default Table_8;
